/**
 * BadgeNotification Component
 *
 * Toast notification that appears when user earns a new badge.
 * Uses WebSocket badge:earned event with connection state monitoring.
 *
 * Handles WebSocket disconnection gracefully:
 * - Shows subtle indicator when notifications may be delayed
 * - Queues notifications received on reconnection
 */

import React, { useState, useEffect, useCallback } from 'react'
import webSocketService from '../../services/webSocketService'

// Timing constants
const NOTIFICATION_DURATION_MS = 5000 // Auto-dismiss after 5 seconds
const ANIMATION_DURATION_MS = 300 // Slide-out animation duration

const RARITY_COLORS = {
  COMMON: 'from-gray-400 to-gray-500',
  UNCOMMON: 'from-green-400 to-green-600',
  RARE: 'from-blue-400 to-blue-600',
  EPIC: 'from-purple-400 to-purple-600',
  LEGENDARY: 'from-yellow-400 via-orange-500 to-red-500'
}

const BadgeNotification = () => {
  const [notifications, setNotifications] = useState([])
  const [isConnected, setIsConnected] = useState(webSocketService.connected)

  const addNotification = useCallback((badge, totalPoints) => {
    const id = Date.now()
    setNotifications((prev) => [...prev, { id, badge, totalPoints, visible: true }])

    // Auto-dismiss after notification duration
    setTimeout(() => {
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, visible: false } : n)))
      // Remove from DOM after animation completes
      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id))
      }, ANIMATION_DURATION_MS)
    }, NOTIFICATION_DURATION_MS)
  }, [])

  useEffect(() => {
    const handleBadgeEarned = (data) => {
      if (data.badge) {
        addNotification(data.badge, data.totalPoints)
      }
    }

    // Track connection state for user feedback
    const handleConnect = () => setIsConnected(true)
    const handleDisconnect = () => setIsConnected(false)

    webSocketService.on('badge:earned', handleBadgeEarned)
    webSocketService.onConnect(handleConnect)
    webSocketService.onDisconnect(handleDisconnect)

    return () => {
      webSocketService.off('badge:earned', handleBadgeEarned)
      webSocketService.offConnect(handleConnect)
      webSocketService.offDisconnect(handleDisconnect)
    }
  }, [addNotification])

  const dismissNotification = (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, visible: false } : n)))
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id))
    }, ANIMATION_DURATION_MS)
  }

  // Show nothing if no notifications and connected
  // Show subtle indicator only when disconnected and no notifications
  if (notifications.length === 0) {
    // Show subtle disconnected indicator in corner
    if (!isConnected) {
      return (
        <div
          className="fixed bottom-4 right-4 z-40"
          role="status"
          aria-live="polite"
          aria-label="Badge notifications may be delayed due to connection issues"
        >
          <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-100 text-yellow-800 text-xs rounded-full shadow-sm">
            <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
            <span>Notifications delayed</span>
          </div>
        </div>
      )
    }
    return null
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-3"
      role="region"
      aria-label="Badge notifications"
    >
      {notifications.map((notification) => (
        <div
          key={notification.id}
          role="alert"
          aria-live="assertive"
          className={`
            transform transition-all duration-300 ease-out
            ${notification.visible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
          `}
        >
          <div
            className={`
              flex items-center gap-4 p-4 rounded-lg shadow-xl
              bg-gradient-to-r ${RARITY_COLORS[notification.badge.rarity] || RARITY_COLORS.COMMON}
              text-white min-w-[300px] max-w-[400px]
            `}
          >
            {/* Badge Icon */}
            <div className="flex-shrink-0">
              {notification.badge.iconPath ? (
                <img
                  src={notification.badge.iconPath}
                  alt=""
                  aria-hidden="true"
                  className="w-12 h-12 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'flex'
                  }}
                />
              ) : null}
              <div
                className={`w-12 h-12 rounded-full bg-white/20 items-center justify-center ${notification.badge.iconPath ? 'hidden' : 'flex'}`}
                aria-hidden="true"
              >
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm uppercase tracking-wide opacity-75">Badge Earned!</p>
              <p className="font-semibold text-lg truncate">{notification.badge.name}</p>
              <p className="text-sm opacity-75 truncate">{notification.badge.description}</p>
              <div className="flex items-center gap-2 mt-1 text-xs opacity-75">
                <span className="capitalize">{notification.badge.rarity?.toLowerCase()}</span>
                <span>+{notification.badge.pointsValue} pts</span>
                {notification.totalPoints && (
                  <span className="border-l border-white/30 pl-2">
                    Total: {notification.totalPoints}
                  </span>
                )}
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={() => dismissNotification(notification.id)}
              className="flex-shrink-0 p-1 rounded-full hover:bg-white/20 transition-colors"
              aria-label={`Dismiss ${notification.badge.name} notification`}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * BadgeNotification - Self-contained notification component
 * No props required - subscribes to WebSocket 'badge:earned' events internally.
 * The component manages its own state and connection to WebSocket.
 *
 * Internal notification shape (for documentation):
 * - id: number (timestamp-based unique ID)
 * - badge: { code, name, description, rarity, pointsValue, iconPath }
 * - totalPoints: number
 * - visible: boolean
 */

export default BadgeNotification
